import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { MapContainer, TileLayer, Marker, GeoJSON, useMapEvents } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import Select from 'react-select';
import L from 'leaflet';

const MAIN_CATEGORIES = [
  "Association",
  "SAS",
  "SARL",
  "CCAS "
];

// Icône personnalisée : rond violet
const purpleCircleIcon = new L.DivIcon({
  html: '<div style="width:22px;height:22px;background:#A32A87;border-radius:50%;box-shadow:0 2px 8px #a32a8722;"></div>',
  className: '',
  iconSize: [22, 22],
  iconAnchor: [11, 11],
  popupAnchor: [0, -11],
});

const Layout = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: center;
  align-items: flex-start;
  width: 100%;
  min-height: auto;
  box-sizing: border-box;
  @media (max-width: 1200px) {
    flex-direction: column;
    align-items: center;
    min-height: auto;
    width: 100%;
    padding: 0 10px;
  }
`;

const Sidebar = styled.div`
  min-width: 320px;
  max-width: 500px;
  background: #faf6fa;
  border-radius: 24px;
  box-shadow: 0 2px 16px #0001;
  padding: 32px 24px 16px 24px;
  margin: 0 32px 0 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 18px;
  height: 600px;
  @media (max-width: 1200px) {
    width: 100%;
    margin: 0 auto 24px auto;
    min-width: unset;
    max-width: unset;
    height: auto;
    padding: 24px 16px;
    box-sizing: border-box;
  }
`;

const MapArea = styled.div`
  flex: 1;
  display: flex;
  align-items: stretch;
  justify-content: center;
  position: relative;
  max-width: 700px;
  min-width: 400px;
  margin-right: 0;
  background: #fff;
  border-radius: 24px;
  box-shadow: 0 2px 16px #0001;
  @media (max-width: 1200px) {
    width: 100%;
    min-width: unset;
    max-width: unset;
    margin: 0 auto;
    box-sizing: border-box;
  }
`;

const StyledMapContainer = styled(MapContainer)`
  width: 100%;
  height: 600px;
  border-radius: 24px;
  z-index: 1;
  @media (max-width: 1200px) {
    height: 60vh;
  }
`;

const MiniMapContainer = styled(MapContainer)`
  width: 100%;
  height: 200px;
  border-radius: 12px;
  margin-bottom: 16px;
  z-index: 1;
  background: #faf6fa;
  @media (max-width: 1200px) {
    height: 180px;
  }
`;

const Label = styled.span`
  font-weight: 600;
  color: #232323;
  margin-right: 8px;
  font-size: 1.1rem;
`;

const MainCatButton = styled.button`
  background: ${({ active }) => (active ? '#A32A87' : '#fff')};
  color: ${({ active }) => (active ? '#fff' : '#A32A87')};
  border: 2px solid #A32A87;
  border-radius: 24px;
  padding: 10px 32px;
  font-weight: 900;
  font-size: 1.1rem;
  cursor: pointer;
  margin-bottom: 8px;
  transition: all 0.15s;
  box-shadow: ${({ active }) => (active ? '0 2px 8px #a32a8722' : 'none')};
`;

const Separator = styled.div`
  height: 40px;
  width: 2px;
  background: #A32A87;
  margin: 0 16px;
  align-self: center;
`;

const FilterBar = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 18px;
  @media (max-width: 1200px) {
    flex-direction: column;
    width: 100%;
  }
`;

const GroupBadge = styled.div`
  background: #A32A87;
  color: #fff;
  font-weight: 700;
  border-radius: 16px;
  padding: 8px 24px;
  margin-bottom: 16px;
  display: flex;
  align-items: center;
  gap: 16px;
`;

const ExitFilterButton = styled.button`
  background: #fff;
  color: #A32A87;
  border: 2px solid #A32A87;
  border-radius: 16px;
  font-weight: 700;
  padding: 6px 18px;
  cursor: pointer;
`;

const InfoPanel = styled.div`
  position: absolute;
  top: 32px;
  right: 32px;
  min-width: 320px;
  max-width: 400px;
  background: #fff;
  border-radius: 18px;
  box-shadow: 0 4px 32px #0002;
  padding: 28px 24px 18px 24px;
  z-index: 10;
  @media (max-width: 900px) {
    position: static;
    margin: 0 auto 24px auto;
    width: 100%;
    top: unset;
    right: unset;
    box-sizing: border-box;
    padding: 20px 16px;
  }
`;

const InfoClose = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  color: #A32A87;
  font-size: 1.5rem;
  font-weight: 900;
  cursor: pointer;
`;

const InfoTitle = styled.h3`
  color: #A32A87;
  font-size: 1.3rem;
  margin: 0 0 12px 0;
`;

const InfoTable = styled.table`
  font-size: 1.05rem;
  border-collapse: collapse;
  width: 100%;
  td {
    padding: 2px 6px;
    vertical-align: top;
  }
  tr:not(:last-child) td {
    border-bottom: 1px solid #eee;
  }
  td:first-child {
    font-weight: 600;
    color: #A32A87;
    white-space: nowrap;
  }

  @media (max-width: 768px) {
    font-size: 0.95rem;
    td {
      padding: 4px 2px;
    }
    td:first-child {
      white-space: normal;
      padding-right: 8px;
    }
    td:last-child {
      word-break: break-word;
    }
  }
`;

const MapOverlayMessage = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  z-index: 2;
  background: rgba(255, 255, 255, 0.8);
  padding: 20px;
  border-radius: 12px;
  box-shadow: 0 4px 16px rgba(0,0,0,0.1);
  text-align: center;
  color: #A32A87;
  font-size: 1.2rem;
  max-width: 80%;
`;

const ContactMessage = styled.div`
  width: 100%;
  background: #faf6fa;
  padding: 24px;
  margin-top: 32px;
  text-align: center;
  font-size: 1.1rem;
  line-height: 1.6;
  color: #232323;
  border-top: 2px solid #A32A87;
  @media (max-width: 768px) {
    padding: 16px;
    font-size: 1rem;
  }
`;

const PopupOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const PopupContent = styled.div`
  background: white;
  padding: 32px;
  border-radius: 24px;
  max-width: 500px;
  text-align: center;
  position: relative;
  box-shadow: 0 4px 32px rgba(0, 0, 0, 0.2);
  @media (max-width: 600px) {
    max-width: 90%;
    padding: 24px;
  }
`;

const PopupClose = styled.button`
  position: absolute;
  top: 16px;
  right: 16px;
  background: none;
  border: none;
  color: #A32A87;
  font-size: 1.5rem;
  font-weight: 900;
  cursor: pointer;
`;

const PopupTitle = styled.h3`
  color: #A32A87;
  font-size: 1.4rem;
  margin: 0 0 16px 0;
`;

const PopupText = styled.p`
  font-size: 1.1rem;
  line-height: 1.6;
  color: #232323;
  margin: 0;
`;

const CartographieTitle = styled.h2`
  text-align: center;
  margin-bottom: 12px;
  font-size: 2.8rem;
  font-weight: 700;

  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const CartographieSubtitle = styled.h4`
  text-align: center;
  margin-bottom: 32px;
  font-size: 1.2rem;
  color: #666;

  @media (max-width: 768px) {
    font-size: 1rem;
    margin-bottom: 24px;
  }
`;

function RegionsGeoJson({ onRegionClick, selectedRegionCode }) {
  const [geojson, setGeojson] = useState(null);
  const geoJsonLayerRef = useRef(null);
  const map = useMapEvents({
    load: (e) => {
      if(geojson) {
        e.target.fitBounds(geoJsonLayerRef.current.getBounds(), { padding: [10, 10] });
      }
    }
  });

  useEffect(() => {
    fetch('/regions-version-simplifiee.geojson')
      .then(res => res.json())
      .then(data => {
        setGeojson(data);
        if(geoJsonLayerRef.current && map) {
          map.fitBounds(geoJsonLayerRef.current.getBounds(), { padding: [10, 10] });
        }
      });
  }, [map]);

  const regionStyle = useCallback((feature) => {
    return {
      fillColor: selectedRegionCode === feature.properties.code ? '#A32A87' : 'transparent',
      weight: selectedRegionCode === feature.properties.code ? 2 : 1,
      opacity: 1,
      color: selectedRegionCode === feature.properties.code ? '#7a1e62' : '#A32A87',
      fillOpacity: selectedRegionCode === feature.properties.code ? 0.7 : 0
    };
  }, [selectedRegionCode]);

  const onEachFeature = (feature, layer) => {
    layer.on({
      click: (e) => {
        onRegionClick(feature.properties.code, feature.properties.nom);
      },
      mouseover: (e) => {
        const layer = e.target;
        layer.setStyle({
          weight: 2,
          color: '#7a1e62',
          fillOpacity: 0.7
        });
      },
      mouseout: (e) => {
        if (selectedRegionCode !== e.target.feature.properties.code) {
          geoJsonLayerRef.current.resetStyle(e.target);
        } else {
          layer.setStyle(regionStyle(e.target.feature));
        }
      }
    });
  };

  useEffect(() => {
    if (geojson && geoJsonLayerRef.current) {
      geoJsonLayerRef.current.eachLayer(layer => {
        geoJsonLayerRef.current.resetStyle(layer);
        if(selectedRegionCode === layer.feature.properties.code) {
          layer.setStyle(regionStyle(layer.feature));
        }
      });
    }
  }, [selectedRegionCode, geojson, regionStyle]);

  return geojson ? (
    <GeoJSON
      data={geojson}
      style={regionStyle}
      onEachFeature={onEachFeature}
      key={JSON.stringify(selectedRegionCode)}
      ref={geoJsonLayerRef}
    />
  ) : null;
}

export default function Cartographie() {
  const [agences, setAgences] = useState([]);
  const [filtreCats, setFiltreCats] = useState([]);
  const [categories, setCategories] = useState([]);
  const [filtreRegion, setFiltreRegion] = useState(null);
  const [groupFilter, setGroupFilter] = useState(null);
  const [selectedAgence, setSelectedAgence] = useState(null);
  const [showPopup, setShowPopup] = useState(false);
  const [popupInfo, setPopupInfo] = useState(null);

  useEffect(() => {
    fetch('/data.json')
      .then(res => res.json())
      .then(data => {
        setAgences(data);
        const cats = Array.from(new Set(data.map(a => a.nomcategorieJuridiqueUniteLegale).filter(v => v && v.trim() !== '')));
        setCategories(cats);
      });
  }, []);

  const mainCats = MAIN_CATEGORIES;
  const otherCats = categories.filter(cat => !mainCats.includes(cat));

  let agencesFiltrees = [];
  const shouldShowResults = groupFilter || (filtreRegion && filtreCats.length > 0);

  if (shouldShowResults) {
    if (groupFilter) {
      agencesFiltrees = agences.filter(a =>
        (groupFilter.siren && a.siren === groupFilter.siren) ||
        (groupFilter.groupName && a["Tête de groupe nationale - Nom"] === groupFilter.groupName)
      );
    } else {
      agencesFiltrees = agences.filter(a => {
        const numRegion = a["NuméroRégion"];
        const regionMatch = filtreRegion ? (numRegion !== undefined && String(Math.round(numRegion)).padStart(2, '0') === filtreRegion.code) : true;
        const categoryMatch = filtreCats.length > 0 ? filtreCats.includes(a.nomcategorieJuridiqueUniteLegale) : true;
        return regionMatch && categoryMatch;
      });
    }
  }

  const selectOptions = otherCats.map(cat => ({ value: cat, label: cat }));

  const customSelectStyles = {
    control: (provided) => ({
      ...provided,
      minWidth: 220,
      borderRadius: 24,
      borderColor: '#A32A87',
      boxShadow: '0 2px 8px #0001',
      fontWeight: 700,
      fontSize: '1.1rem',
      zIndex: 100,
    }),
    menuPortal: base => ({ ...base, zIndex: 9999 }),
    multiValue: (provided) => ({
      ...provided,
      background: '#A32A87',
      color: '#fff',
      borderRadius: 16,
    }),
    multiValueLabel: (provided) => ({
      ...provided,
      color: '#fff',
      fontWeight: 700,
    }),
    multiValueRemove: (provided) => ({
      ...provided,
      color: '#fff',
      ':hover': { backgroundColor: '#7a1e62', color: 'white' },
    }),
    option: (provided, state) => ({
      ...provided,
      background: state.isSelected ? '#A32A87' : state.isFocused ? '#e9d1e3' : '#fff',
      color: state.isSelected ? '#fff' : '#A32A87',
      fontWeight: state.isSelected ? 700 : 500,
      fontSize: '1.1rem',
    }),
  };

  const toggleMainCat = (cat) => {
    if (filtreCats.includes(cat)) {
      setFiltreCats(filtreCats.filter(c => c !== cat));
    } else {
      setFiltreCats([...filtreCats, cat]);
    }
    setGroupFilter(null);
  };

  const handleRegionClick = (code, nom) => {
    setFiltreRegion({ code, nom });
    setGroupFilter(null);
  };

  const handleShowGroupOrEtab = (agence) => {
    setPopupInfo({
      groupName: agence["Tête de groupe nationale - Nom"] || agence.denominationUniteLegale,
      nbEtablissements: agence.nb_etabs_groupe || 0,
      nbFiliales: agence.nb_filiales_groupe || 0
    });
    setShowPopup(true);
  };

  const handleExitFilter = () => {
    setFiltreRegion(null);
    setFiltreCats([]);
  };

  const handleExitGroup = () => {
    setGroupFilter(null);
    setSelectedAgence(null);
  };

  const handleMarkerClick = (agence) => {
    setSelectedAgence(agence);
  };

  const handleCloseInfo = () => {
    setSelectedAgence(null);
  };

  const handleClosePopup = () => {
    setShowPopup(false);
  };

  return (
    <>
      <CartographieTitle>Cartographie des agences d'aide à domicile en France</CartographieTitle>
      <CartographieSubtitle>Cliquez sur une agence pour obtenir ses informations</CartographieSubtitle>
      <Layout>
        <Sidebar>
          <Label style={{alignSelf:'flex-start'}}>Sélectionner une région :</Label>
          <MiniMapContainer center={[47, 2]} zoom={4} zoomControl={false} attributionControl={false} scrollWheelZoom={false} dragging={false} doubleClickZoom={false} touchZoom={false}>
            <TileLayer url="" />
            <RegionsGeoJson onRegionClick={handleRegionClick} selectedRegionCode={filtreRegion?.code} />
          </MiniMapContainer>
          {filtreRegion && (
            <GroupBadge style={{alignSelf:'center', width:'100%', justifyContent:'space-between'}}>
              <span>Région sélectionnée : <b>{filtreRegion.nom}</b></span>
              <ExitFilterButton onClick={handleExitFilter}>X</ExitFilterButton>
            </GroupBadge>
          )}

          <Label style={{alignSelf:'flex-start'}}>Catégorie juridique :</Label>
          <FilterBar>
            {mainCats.map(cat => (
              <MainCatButton
                key={cat}
                active={filtreCats.includes(cat)}
                onClick={() => toggleMainCat(cat)}
                disabled={!!groupFilter}
              >
                {cat}
              </MainCatButton>
            ))}
            {otherCats.length > 0 && <Separator />}
            <div style={{ minWidth: 220, maxWidth: 400, zIndex: 100 }}>
              <Select
                isMulti
                options={selectOptions}
                value={selectOptions.filter(opt => filtreCats.includes(opt.value))}
                onChange={opts => setFiltreCats(opts.map(o => o.value))}
                placeholder="Autres catégories..."
                styles={customSelectStyles}
                closeMenuOnSelect={false}
                noOptionsMessage={() => 'Aucune catégorie'}
                menuPortalTarget={document.body}
                isDisabled={!!groupFilter}
              />
            </div>
          </FilterBar>

          {groupFilter && (
            <GroupBadge style={{alignSelf:'center', width:'100%', justifyContent:'space-between'}}>
              <span>Affichage du groupe : <b>{groupFilter.siren}</b></span>
              {groupFilter.groupName && <span>et du groupe : <b>{groupFilter.groupName}</b></span>}
              <ExitFilterButton onClick={handleExitGroup}>X</ExitFilterButton>
            </GroupBadge>
          )}
        </Sidebar>

        <MapArea>
          <StyledMapContainer center={[47, 2]} zoom={5.5} scrollWheelZoom>
            <TileLayer
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {shouldShowResults && agencesFiltrees.filter(a => a.latitude && a.longitude).map(agence => (
              <Marker
                key={agence.siret}
                position={[agence.latitude, agence.longitude]}
                icon={purpleCircleIcon}
                eventHandlers={{ click: () => handleMarkerClick(agence) }}
              />
            ))}
          </StyledMapContainer>
          {!shouldShowResults && !groupFilter && (
            <MapOverlayMessage>
              Veuillez sélectionner une région ET au moins une catégorie juridique<br/>pour afficher les agences sur la carte.
            </MapOverlayMessage>
          )}
          {selectedAgence && (
            <InfoPanel>
              <InfoClose onClick={handleCloseInfo} title="Fermer">×</InfoClose>
              <InfoTitle>{selectedAgence.denominationUniteLegale}</InfoTitle>
              <InfoTable>
                <tbody>
                  {selectedAgence["Tête de groupe nationale - Nom"] && <tr><td>Tête de groupe</td><td>{selectedAgence["Tête de groupe nationale - Nom"]}</td></tr>}
                  {selectedAgence.dateCreationEtablissement && <tr><td>Date création</td><td>{selectedAgence.dateCreationEtablissement}</td></tr>}
                  {selectedAgence.codePostalEtablissement && <tr><td>Code postal</td><td>{selectedAgence.codePostalEtablissement}</td></tr>}
                  {selectedAgence.libelleCommuneEtablissement && <tr><td>Commune</td><td>{selectedAgence.libelleCommuneEtablissement}</td></tr>}
                  {selectedAgence.siret && <tr><td>SIRET</td><td>{selectedAgence.siret}</td></tr>}
                  {selectedAgence.siren && <tr><td>SIREN</td><td>{selectedAgence.siren}</td></tr>}
                  {selectedAgence.categorieEntreprise && <tr><td>Catégorie entreprise</td><td>{selectedAgence.categorieEntreprise}</td></tr>}
                  {selectedAgence.nomcategorieJuridiqueUniteLegale && <tr><td>Cat. Juridique</td><td>{selectedAgence.nomcategorieJuridiqueUniteLegale}</td></tr>}
                  {selectedAgence.nb_etabs_siren !== undefined && <tr><td>Nb étab. SIREN</td><td>{selectedAgence.nb_etabs_siren}</td></tr>}
                  {selectedAgence.nb_filiales_groupe !== undefined && <tr><td>Nb filiales groupe</td><td>{selectedAgence.nb_filiales_groupe}</td></tr>}
                  {selectedAgence.nb_etabs_groupe !== undefined && <tr><td>Nb étab. groupe</td><td>{selectedAgence.nb_etabs_groupe}</td></tr>}
                </tbody>
              </InfoTable>
              {(selectedAgence.nb_etabs_siren > 1 || selectedAgence.nb_filiales_groupe > 1) && (
                <div style={{marginTop:12, textAlign:'center'}}>
                  <button
                    style={{background:'#A32A87',color:'#fff',border:'none',borderRadius:12,padding:'8px 18px',fontWeight:700,cursor:'pointer'}}
                    onClick={() => handleShowGroupOrEtab(selectedAgence)}
                  >
                    Voir tous les établissements/filiales
                  </button>
                </div>
              )}
            </InfoPanel>
          )}
        </MapArea>
      </Layout>


      {showPopup && popupInfo && (
        <PopupOverlay>
          <PopupContent>
            <PopupClose onClick={handleClosePopup}>×</PopupClose>
            <PopupTitle>Accès aux données complètes</PopupTitle>
            <PopupText>
              Nous avons identifié {popupInfo.nbEtablissements} agences et {popupInfo.nbFiliales} SIREN pour le groupe <strong>{popupInfo.groupName}</strong>.
              <br /><br />
              Pour accéder aux données complètes des établissements et filiales, veuillez contacter Domicare au +33 6 74 83 93 29 ou par email : hugo.peeters@domicare.fr
            </PopupText>
          </PopupContent>
        </PopupOverlay>
      )}
    </>
  );
} 